'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ConnectSetupChecklist } from '@/platform/vendor/ConnectSetupChecklist';
import { getConnectStatus, syncVendorConnect } from '@/platform/vendor/api';
import { readHaulSellResume } from '@/platform/vendor/sellerResume';

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 15;

type Props = {
  vendorUserId: string;
  onReady?: () => void;
};

async function loadVendorConnectReady(vendorUserId: string, syncFromStripe: boolean) {
  if (syncFromStripe) {
    await syncVendorConnect(vendorUserId).catch(() => null);
  }
  const connect = await getConnectStatus(vendorUserId);
  return connect.connectOnboardingComplete;
}

export function VendorConnectReturnHandler({ vendorUserId, onReady }: Props) {
  const searchParams = useSearchParams();
  const connect = searchParams.get('connect');
  const haulConnect = searchParams.get('haulConnect');
  const sellerParam = searchParams.get('seller');
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  const [pollTick, setPollTick] = useState(0);
  const resume = ready && haulConnect ? readHaulSellResume() : null;

  const active =
    ((connect === 'return' || connect === 'refresh') && (!sellerParam || sellerParam === vendorUserId)) ||
    haulConnect === 'return' ||
    haulConnect === 'refresh';

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    let attempts = 0;
    setLoading(true);

    async function poll() {
      try {
        const isReady = await loadVendorConnectReady(vendorUserId, attempts === 0);
        if (cancelled) return;
        setReady(isReady);
        setPollTick((n) => n + 1);
        setError('');
        if (isReady) {
          onReady?.();
          setLoading(false);
          return;
        }
        if (attempts >= POLL_MAX_ATTEMPTS) {
          setLoading(false);
          return;
        }
        attempts += 1;
        window.setTimeout(() => void poll(), POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Could not check payment setup');
        setLoading(false);
      }
    }

    void poll();
    return () => {
      cancelled = true;
    };
  }, [active, vendorUserId, onReady]);

  if (!active) return null;

  async function manualRefresh() {
    setLoading(true);
    setError('');
    try {
      const isReady = await loadVendorConnectReady(vendorUserId, true);
      setReady(isReady);
      setPollTick((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not refresh payment setup');
    } finally {
      setLoading(false);
    }
  }

  const message =
    error ||
    'Stripe Connect setup is not complete yet. Finish onboarding from the seller dashboard, then refresh.';

  return (
    <div
      className={`feed-card settings-page vendor-connect-return${ready ? ' vendor-connect-return--ready' : ''}`}
      role="status"
    >
      {loading && <p className="text-muted">Confirming Stripe Connect setup…</p>}
      {!loading && ready && (
        <p>
          <strong>Payment setup complete</strong>. You can publish products and receive payouts.
        </p>
      )}
      {!loading && !ready && (error ? <p className="form-error">{message}</p> : <p className="text-muted">{message}</p>)}
      {ready && resume && (
        <p>
          <Link href={`/claims/${resume.claimId}/#find-${resume.lineId}`} className="btn btn-primary">
            Return to your haul report
          </Link>
        </p>
      )}
      <ConnectSetupChecklist vendorUserId={vendorUserId} refreshKey={pollTick} />
      {!loading && !ready && (
        <p>
          <button type="button" className="btn btn-secondary" onClick={() => void manualRefresh()}>
            Refresh status
          </button>
        </p>
      )}
    </div>
  );
}
